import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom"; // Keep this
import SummaryApi from "../common";
import noimage from "../asset/noimage.png";
import { MdCheckCircle, MdDownload } from "react-icons/md";
import { FiEdit, FiTrash2, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaGlobe } from "react-icons/fa";
import { BsPersonCheckFill } from "react-icons/bs";
import instructorImage from "../asset/person.png";
import { decodeCourseId } from "../utils/encoder";
import { FaStar } from "react-icons/fa";
import { FaLevelUpAlt } from "react-icons/fa";
import { GiDuration } from "react-icons/gi";

const AdminCourseDetails = () => {
  const [course, setCourse] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [topics, setTopics] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [tools, setTools] = useState([]);
  const [instructor, setInstructor] = useState(null);

  const [searchParams] = useSearchParams();
  const encodedId = searchParams.get("id");
  const courseId = encodedId ? decodeCourseId(encodedId) : null;

  useEffect(() => {
    if (!course) return;

    const fetchCourseDetails = async () => {
      try {
        // Fetch Curriculum
        const curriculumRes = await fetch(
          SummaryApi.getCurriculumByCourseId.url,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ course_id: course.course_id }),
          }
        );
        const curriculumData = await curriculumRes.json();

        // Fetch Lessons
        const lessonRes = await fetch(SummaryApi.getLessonByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const lessonData = await lessonRes.json();

        // Organize curriculum & lessons
        if (curriculumData.status) {
          const groupedLessons = (
            lessonData.status ? lessonData.data : []
          ).reduce((acc, lesson) => {
            if (!acc[lesson.curriculum_id]) {
              acc[lesson.curriculum_id] = [];
            }
            acc[lesson.curriculum_id].push(lesson);
            return acc;
          }, {});

          // === NEW: Calculate total duration per topic ===
          const calculateTotalDuration = (lessons) => {
            return lessons.reduce((sum, l) => sum + (parseInt(l.duration) || 0), 0);
          };

          const structuredTopics = curriculumData.data.map((curr) => {
            const lessons = groupedLessons[curr.curriculum_id] || [];
            const totalDuration = calculateTotalDuration(lessons); // Auto-calculate

            return {
              id: curr.curriculum_id,
              title: curr.topic_name,
              topicDuration: curr.duration,
              lessons: lessons,
              isOpen: false,
              totalDuration: totalDuration, // Auto-calculated
            };
          });
          // === END NEW ===

          setTopics(structuredTopics);
        }

        // ❓ Fetch FAQs
        const faqRes = await fetch(SummaryApi.getFaqsByCourseID.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const faqData = await faqRes.json();
        if (faqData.status) setFaqs(faqData.data);

        // Fetch Tools
        const toolRes = await fetch(SummaryApi.getToolsByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const toolData = await toolRes.json();
        if (toolData.status) setTools(toolData.data);

        // Fetch Instructor
        const instructorRes = await fetch(SummaryApi.instructorByCourseId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ course_id: course.course_id }),
        });
        const instructorData = await instructorRes.json();
        if (
          instructorData.status &&
          Array.isArray(instructorData.data) &&
          instructorData.data.length > 0
        ) {
          setInstructor(instructorData.data[0]); // Set first instructor
        } else {
          console.warn("No instructor data found for this course");
        }
      } catch (err) {
        console.error("Failed to fetch course details:", err);
      }
    };

    fetchCourseDetails();
  }, [course]);

  const parseSkills = (htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString;
    const listItems = temp.querySelectorAll("li");

    return Array.from(listItems).map((li) => li.textContent.trim());
  };

  const toggleTopic = (id) => {
    setTopics((prev) =>
      prev.map((topic) => ({
        ...topic,
        isOpen: topic.id === id,
      }))
    );
  };

  useEffect(() => {
    // 1. Check if courseId exists (decoded value)
    if (!courseId) {
      console.error("Course ID is missing or invalid.");
      return; // Stops here if no valid courseId
    }

    // 2. Fetch all active courses from the API
    fetch(SummaryApi.getActiveCourses.url)
      .then((res) => {
        // Log response status to ensure it's OK
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        // 3. Log the API response data for inspection
        console.log("API Response (getActiveCourses):", data);

        // Check if data.data exists and is an array
        if (!data.status || !Array.isArray(data.data)) {
          console.error("API response structure is not as expected:", data);
          return;
        }

        // 4. Try to find the matching course
        const found = data.data.find((c) => String(c.course_id) === courseId);
        // Log the result of the find operation
        console.log(
          "Attempted to find course with ID",
          courseId,
          ". Found:",
          found
        );

        // 5. If found, set the course state
        if (found) {
          setCourse(found);
        } else {
          // 6. If not found, log an error
          console.error(
            "Course not found in active courses list for ID:",
            courseId
          );
        }
      })
      .catch((err) => {
        // 7. Catch any errors during fetch or JSON parsing
        console.error("Failed to fetch course data:", err);
      });
  }, [courseId]); // Effect runs when courseId changes

  useEffect(() => {
    if (!courseId) {
      // This check will use the *decoded* courseId
      console.error("Course ID is missing or invalid.");
      return;
    }

    fetch(SummaryApi.getActiveCourses.url)
      .then((res) => res.json())
      .then((data) => {
        // This comparison uses the *decoded* courseId
        const found = data.data.find((c) => String(c.course_id) === courseId);
        if (found) setCourse(found);
        else console.error("Course not found with ID:", courseId);
      })
      .catch((err) => console.error("Failed to fetch course:", err));
  }, [courseId]); // This effect re-runs when the *decoded* courseId changes

  if (!course) {
    return (
      <div className="p-10 text-center text-gray-600">
        Loading course details...
      </div>
    );
  }

  // Feature list item component
  function FeatureItem({ text }) {
    return (
      <div className="flex items-center gap-2">
        <MdCheckCircle className="text-green-500 w-5 h-5" />
        <span>{text}</span>
      </div>
    );
  }

  const parseStudentLearning = (htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString;
    const listItems = temp.querySelectorAll("li");

    if (listItems.length > 0) {
      return Array.from(listItems).map((li) => li.textContent.trim());
    } else {
      // Fallback to paragraphs or plain text
      const paragraphs = temp.querySelectorAll("p");
      if (paragraphs.length > 0) {
        return Array.from(paragraphs).map((p) => p.textContent.trim());
      } else {
        // Just return the raw text as a single item
        return [temp.textContent.trim()];
      }
    }
  };

  return (
    <div>
      {/* Header Section */}
      <section className="bg-[#0F3557] text-white flex items-center justify-between px-10 py-12 rounded-lg">
        {/* Left Content */}
        <div className="max-w-lg space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            {course.course_title}
          </h1>

          <p className="text-[#B1BFCB] text-lg">
            {course.subtitle ||
              "Master the latest technologies with our comprehensive certification program designed by industry experts."}
          </p>
          <div className="flex items-center space-x-6 text-yellow-400 font-semibold">
            <div className="flex items-center space-x-1">
              <span className="text-2xl">
                <GiDuration />
              </span>
              <span className="text-lg text-yellow-400">
                {course.course_duration}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-2xl">
                <FaLevelUpAlt />
              </span>
              <span className="text-lg text-yellow-400">
                {course.diffculty_level}
              </span>
            </div>
          </div>

          {/* <p>{course.course_type}</p> */}

          <div className="flex items-center space-x-6 text-yellow-400 font-semibold">
            <div className="flex items-center space-x-1">
              <span className="text-2xl">
                <FaStar />
              </span>
              <span className="text-lg text-yellow-400">{course.rating}</span>
            </div>
          </div>
        </div>

        <div>
          <img
            src={
              course.course_thumbnail
                ? `${SummaryApi.Images.url.replace(
                    "api",
                    ""
                  )}${course.course_thumbnail.replace("/api", "")}`
                : noimage
            }
            alt={course.course_title}
            className="w-full h-64 object-cover rounded-lg mb-6"
          />
        </div>
      </section>

      {/* Main Content Section */}
      <div className=" bg-gray-100 p-8 flex justify-center items-start">
        <div className="max-w-8xl w-full flex gap-10">
          {/* Left Section */}
          <div className="flex-1 bg-white rounded-lg shadow p-8">
            {/* Tabs */}
            <div className="flex border border-gray-200 rounded-md overflow-hidden w-full max-w-md mb-8">
              {["overview", "curriculum", "instructor", "review"].map((tab) => (
                <button
                  key={tab}
                  className={`flex-1 py-3 px-4 font-medium transition ${
                    activeTab === tab
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-600"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "overview" && (
                <div className="max-w-8xl w-full">
                  <h2 className="bg-blue-800 text-white font-bold text-xl rounded-t-md p-4 mb-4">
                    Course Overview
                  </h2>
                  <p className="text-gray-700 mb-2 leading-relaxed max-w-8xl p-2">
                    {course.subtitle}
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed max-w-8xl p-2">
                    {course.description}
                  </p>
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-5 text-lg">
                        What you'll learn
                      </h3>

                      <div className="grid grid-cols-1 gap-y-6 gap-x-10 max-w-xl text-black-700">
                        {parseStudentLearning(course.student_learning).map(
                          (item, idx) => (
                            <FeatureItem key={idx} text={item} />
                          )
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800  mb-5 text-lg">
                        Requirements
                      </h3>

                      <div className="grid grid-cols-1 gap-y-6 gap-x-10 max-w-xl text-black-700">
                        {parseStudentLearning(course.learning_requirements).map(
                          (item, idx) => (
                            <FeatureItem key={idx} text={item} />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    {/* Tools Section */}
                    {tools.length > 0 && (
                      <div className="mt-10">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                          Tools You'll Use
                        </h3>
                        <div className="flex flex-wrap gap-4">
                          {tools.map((tool) => (
                            <img
                              key={tool.tools_id}
                              src={
                                tool.tool_image
                                  ? `${SummaryApi.Images.url.replace(
                                      "api",
                                      ""
                                    )}${tool.tool_image.replace("/api", "")}`
                                  : noimage
                              }
                              onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = noimage;
                              }}
                              alt="Tool"
                              className="h-16 w-auto object-contain border p-2 bg-white rounded shadow"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* FAQs Section */}
                  {faqs.length > 0 && (
                    <div className="mt-10">
                      <h3 className="text-xl font-bold text-gray-800 mb-4">
                        Frequently Asked Questions
                      </h3>

                      <div className="space-y-3">
                        {(showAllFaqs ? faqs : faqs.slice(0, 3)).map((faq) => {
                          const isOpen = openFaqId === faq.information_id;
                          return (
                            <div
                              key={faq.information_id}
                              className="border border-gray-300 rounded-md p-4 cursor-pointer"
                              onClick={() =>
                                setOpenFaqId((prev) =>
                                  prev === faq.information_id
                                    ? null
                                    : faq.information_id
                                )
                              }
                            >
                              <div className="flex justify-between items-center font-semibold text-blue-800">
                                <span>{faq.question}</span>
                                {isOpen ? (
                                  <FiChevronUp className="text-xl" />
                                ) : (
                                  <FiChevronDown className="text-xl" />
                                )}
                              </div>
                              {isOpen && (
                                <div className="mt-2 text-gray-700">
                                  {faq.answer}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {faqs.length > 3 && (
                        <button
                          onClick={() => setShowAllFaqs((prev) => !prev)}
                          className="mt-4 text-blue-600 font-medium hover:underline"
                        >
                          {showAllFaqs ? "View Less" : "View More"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "curriculum" && (
                <div>
                  <h2 className="bg-blue-800 text-white font-bold text-xl rounded-t-md p-4 mb-4">
                    Course Curriculum
                  </h2>
                  {topics.length === 0 ? (
                    <p className="text-gray-600">No curriculum available.</p>
                  ) : (
                    topics.map((topic) => (
                      <div key={topic.id} className="mb-4 border rounded">
                        <div
                          className="flex justify-between items-center bg-gray-100 px-4 py-3 cursor-pointer"
                          onClick={() => toggleTopic(topic.id)}
                        >
                          <h3 className="font-semibold">
                            {topic.title}
                            {/* === CHANGED: Show auto-calculated duration === */}
                            <span className="ml-2 text-sm text-gray-500">
                              ({topic.totalDuration} mins)
                            </span>
                          </h3>
                          {topic.isOpen ? (
                            <FiChevronUp className="text-xl" />
                          ) : (
                            <FiChevronDown className="text-xl" />
                          )}
                        </div>

                        {topic.isOpen && (
                          <div className="bg-white p-4 space-y-2">
                            {topic.lessons.length > 0 ? (
                              topic.lessons.map((lesson) => (
                                <div
                                  key={lesson.lesson_id}
                                  className="flex justify-between items-center bg-green-50 p-2 rounded"
                                >
                                  <div className="flex items-center gap-2 text-green-600">
                                    <div className="h-3 w-3 bg-green-500 rounded-full" />
                                    <span>{lesson.lesson_name}</span>
                                  </div>
                                  <span className="text-sm text-gray-500">
                                    {lesson.duration} mins
                                  </span>
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-500">
                                No lessons found for this topic.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "instructor" && (
                <div className=" bg-white rounded-lg overflow-hidden">
                  {instructor ? (
                    <>
                      <h2 className="bg-blue-800 text-white font-bold text-xl rounded-t-md p-4 mb-6">
                        {instructor.first_name} {instructor.last_name}
                      </h2>

                      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                        <img
                          src={
                            instructor.instructor_image
                              ? `${SummaryApi.Images.url.replace("api", "")}${
                                  instructor.instructor_image
                                }`
                              : instructorImage
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = instructorImage;
                          }}
                          alt={instructor.instructor_name}
                          className="w-40 h-40 rounded-full object-cover border-4 border-blue-800"
                        />

                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            About
                          </h3>
                          <p className="text-gray-700 text-justify leading-relaxed mb-4">
                            {instructor.about_instructor1 ||
                              "No bio available."}
                          </p>
                          <p className="text-gray-700 text-justify leading-relaxed mb-4">
                            {instructor.about_instructor2}
                          </p>

                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <FaPhone className="text-blue-600" />
                              <span>
                                {instructor.mobile_number || "no number"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaEnvelope className="text-blue-600" />
                              <span>{instructor.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaMapMarkerAlt className="text-blue-600" />
                              <span>
                                {instructor.address ||
                                  "Flat No. 203, Sri Lakshmi Residency, Vijayawada – 520010, Andhra Pradesh, India"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaGlobe className="text-blue-600" />
                              <a
                                href={
                                  instructor.website ||
                                  "http://www.teachers.com"
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 hover:underline"
                              >
                                {instructor.website || "www.teachers.com"}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Skills Section */}
                      {/* Skills Section */}
                      {instructor.skills && (
                        <div className="mt-6">
                          <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            Skills
                          </h3>
                          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 list-disc list-inside text-gray-700">
                            {parseSkills(instructor.skills).map(
                              (skill, idx) => (
                                <li key={idx}>{skill}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-600">
                      Instructor details are not available.
                    </p>
                  )}
                </div>
              )}

              {activeTab === "review" && (
                <div>
                  <h2 className="bg-blue-800 text-white font-bold text-xl rounded-t-md p-4 mb-4">
                    Reviews
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {/* Replace this with actual reviews */}
                    User reviews and ratings will be displayed here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCourseDetails;